import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";

import { PreRegisterProvider } from "@/components/PreRegisterModal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";

import Home from "@/pages/Home";
import Taxi360 from "@/pages/Taxi360";
import Passageiros from "@/pages/Passageiros";
import Motoristas from "@/pages/Motoristas";
import Seguranca from "@/pages/Seguranca";
import Futuro from "@/pages/Futuro";
import ComingSoon from "@/pages/ComingSoon";
import Sobre from "@/pages/Sobre";
import Suporte from "@/pages/Suporte";
import LegalPage from "@/pages/LegalPage";
import ExcluirConta from "@/pages/ExcluirConta";
import Admin from "@/pages/Admin";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-brand-obsidian px-6 text-center">
          <p className="font-display text-2xl font-bold text-white">Algo deu errado</p>
          <p className="text-sm text-slate-400">Recarregue a página para continuar navegando.</p>
          <Link to="/" className="rounded-full bg-brand-orange px-6 py-3 text-sm font-bold text-white">
            Voltar ao início
          </Link>
        </div>
      );
    }
    return this.props.children;
  }
}

function LenisScroll() {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);
  useEffect(() => {
    if (pathname.startsWith("/admin")) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 450);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

import { usePageMeta } from "@/lib/seo";

const META = {
  "/": {
    title: "OFF360 — Mobilidade, descontos e serviços perto de você",
    description:
      "Conectando você ao que importa: o 360Taxi é o serviço principal do lançamento do OFF360. Conheça o ecossistema e cadastre-se para os próximos lançamentos.",
  },
  "/360taxi": {
    title: "360Taxi — Mobilidade urbana do OFF360",
    description:
      "Conheça o 360Taxi: corridas por aplicativo com categorias Básico, Select e Premium, segurança e praticidade para passageiros e motoristas parceiros.",
  },
  "/passageiros": {
    title: "Passageiros 360Taxi — Como solicitar corridas",
    description:
      "Saiba como solicitar corridas no 360Taxi: cadastro, escolha de destino e categoria, formas de pagamento e recursos de segurança da sua viagem.",
  },
  "/motoristas": {
    title: "Motoristas 360Taxi — Cadastro e requisitos",
    description:
      "Seja um motorista parceiro 360Taxi: cadastro digital em cinco etapas, análise documental, categorias e modelo comercial por mensalidade, sem comissão.",
  },
  "/seguranca": {
    title: "Segurança — 360Taxi e OFF360",
    description:
      "Recursos de segurança para passageiros e motoristas: verificação cadastral, código de embarque, compartilhamento de viagem e canais de suporte.",
  },
  "/futuro-off360": {
    title: "Futuro OFF360 — Próximos lançamentos",
    description:
      "Comércio e descontos, serviços, entregas, 360Moto, empresários e oportunidades: conheça as frentes futuras do ecossistema OFF360.",
  },
  "/empresarios": {
    title: "OFF360 para Empresários — Em breve",
    description:
      "Ferramentas de divulgação, campanhas e descontos para o seu negócio. O OFF360 para Empresários está em desenvolvimento — registre seu interesse.",
  },
  "/360moto": {
    title: "360Moto — Em breve",
    description:
      "Uma nova forma de se movimentar: mobilidade sobre duas rodas do OFF360 em desenvolvimento. Registre seu interesse para o lançamento.",
  },
  "/entregas": {
    title: "OFF360 Entregas — Em breve",
    description:
      "Soluções de entregas e logística local do OFF360 em desenvolvimento. Deixe seu e-mail para receber o aviso de lançamento.",
  },
  "/sobre": {
    title: "Sobre nós — OFF360",
    description:
      "Conheça a OFF360 TECNOLOGIA SERVICOS E SOLUCOES LTDA, nossa base em Campinas-SP e o propósito de conectar pessoas, negócios e oportunidades.",
  },
  "/suporte": {
    title: "Central de Suporte — OFF360",
    description:
      "Como podemos ajudar? Abra solicitação com protocolo de atendimento, fale pelo WhatsApp oficial ou envie e-mail para a equipe OFF360.",
  },
  "/politica-de-privacidade": {
    title: "Política de Privacidade — OFF360",
    description:
      "Como os dados pessoais poderão ser coletados, utilizados, armazenados, compartilhados e protegidos no site e nos serviços do OFF360 e do 360Taxi.",
  },
  "/termos-passageiros": {
    title: "Termos de Uso — Passageiros — OFF360",
    description:
      "Condições de utilização do 360Taxi pelos passageiros: cadastro, solicitação de corridas, categorias, pagamentos, segurança e responsabilidades.",
  },
  "/termos-motoristas": {
    title: "Termos de Uso — Motoristas — OFF360",
    description:
      "Condições de cadastro e utilização do 360Taxi pelos motoristas parceiros: requisitos, categorias, modelo comercial e recebimentos.",
  },
  "/termos-empresarios": {
    title: "Termos de Uso — Empresários — OFF360",
    description:
      "Condições aplicáveis ao OFF360 para Empresários, serviço em desenvolvimento e ainda não disponível para contratação.",
  },
  "/excluir-conta": {
    title: "Exclusão de Conta e Dados — OFF360",
    description:
      "Solicite a exclusão da sua conta e dos dados pessoais do OFF360/360Taxi pelo formulário público, com protocolo e verificação de titularidade.",
  },
};

function SiteLayout({ children }) {
  const { pathname } = useLocation();
  usePageMeta(META[pathname]);
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LenisScroll />
        <ScrollToTop />
        <PreRegisterProvider>
          <Routes>
            <Route
              path="/"
              element={
                <SiteLayout>
                  <Home />
                </SiteLayout>
              }
            />
            <Route
              path="/360taxi"
              element={
                <SiteLayout>
                  <Taxi360 />
                </SiteLayout>
              }
            />
            <Route
              path="/passageiros"
              element={
                <SiteLayout>
                  <Passageiros />
                </SiteLayout>
              }
            />
            <Route
              path="/motoristas"
              element={
                <SiteLayout>
                  <Motoristas />
                </SiteLayout>
              }
            />
            <Route
              path="/seguranca"
              element={
                <SiteLayout>
                  <Seguranca />
                </SiteLayout>
              }
            />
            <Route
              path="/futuro-off360"
              element={
                <SiteLayout>
                  <Futuro />
                </SiteLayout>
              }
            />
            <Route
              path="/empresarios"
              element={
                <SiteLayout>
                  <ComingSoon cfgKey="empresarios" />
                </SiteLayout>
              }
            />
            <Route
              path="/360moto"
              element={
                <SiteLayout>
                  <ComingSoon cfgKey="360moto" />
                </SiteLayout>
              }
            />
            <Route
              path="/entregas"
              element={
                <SiteLayout>
                  <ComingSoon cfgKey="entregas" />
                </SiteLayout>
              }
            />
            <Route
              path="/sobre"
              element={
                <SiteLayout>
                  <Sobre />
                </SiteLayout>
              }
            />
            <Route
              path="/suporte"
              element={
                <SiteLayout>
                  <Suporte />
                </SiteLayout>
              }
            />
            <Route
              path="/politica-de-privacidade"
              element={
                <SiteLayout>
                  <LegalPage docKey="politica-de-privacidade" />
                </SiteLayout>
              }
            />
            <Route
              path="/termos-passageiros"
              element={
                <SiteLayout>
                  <LegalPage docKey="termos-passageiros" />
                </SiteLayout>
              }
            />
            <Route
              path="/termos-motoristas"
              element={
                <SiteLayout>
                  <LegalPage docKey="termos-motoristas" />
                </SiteLayout>
              }
            />
            <Route
              path="/termos-empresarios"
              element={
                <SiteLayout>
                  <LegalPage docKey="termos-empresarios" />
                </SiteLayout>
              }
            />
            <Route
              path="/excluir-conta"
              element={
                <SiteLayout>
                  <ExcluirConta />
                </SiteLayout>
              }
            />
            <Route path="/admin" element={<Admin />} />
            <Route
              path="*"
              element={
                <SiteLayout>
                  <section className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-6 py-32 text-center">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">
                      404
                    </p>
                    <h1 className="font-display text-3xl font-bold text-slate-900">Página não encontrada</h1>
                    <Link
                      to="/"
                      data-testid="not-found-home-link"
                      className="rounded-full bg-brand-orange px-6 py-3 text-sm font-bold text-white"
                    >
                      Voltar ao início
                    </Link>
                  </section>
                </SiteLayout>
              }
            />
          </Routes>
        </PreRegisterProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
